import __var_0__ from '@stdlib/utils/define-nonenumerable-read-only-property';
import __var_1__ from '@stdlib/assert/is-iterator-like';
import __var_2__ from '@stdlib/assert/is-positive-integer';
import __var_3__ from '@stdlib/assert/is-function';
import __var_4__ from '@stdlib/assert/has-own-property';
import __var_5__ from '@stdlib/symbol/iterator';
import __var_6__ from '@stdlib/stats/incr/mprod';
/**

*
* Copyright (c) 2019 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //
var setReadOnly = __var_0__;
var isIteratorLike = __var_1__;
var isPositiveInteger = __var_2__.isPrimitive;
var isFunction = __var_3__;
var hasOwnProp = __var_4__;
var iteratorSymbol = __var_5__;
var incrmprod = __var_6__;


// MAIN //

/**
* Returns an iterator which iteratively computes a moving product.
*
* @param {Iterator} iterator - input iterator
* @param {PositiveInteger} W - window size
* @throws {TypeError} first argument must be an iterator
* @throws {TypeError} second argument must be a positive integer
* @returns {Iterator} iterator
*
* @example
* var runif = require( '@stdlib/random/iter/uniform' );
*
* var rand = runif( -10.0, 10.0, {
*     'iter': 100
* });
*
* var it = itermprod( rand, 3 );
*
* var v = it.next().value;
* // returns <number>
*
* v = it.next().value;
* // returns <number>
*
* v = it.next().value;
* // returns <number>
*
* // ...
*/
function itermprod( iterator, W ) {
	var iter;
	var FLG;
	var acc;
	if ( !isIteratorLike( iterator ) ) {
		throw new TypeError( 'invalid argument. First argument must be an iterator. Value: `' + iterator + '`.' );
	}
	if ( !isPositiveInteger( W ) ) {
		throw new TypeError( 'invalid argument. Second argument must be a positive integer. Value: `' + W + '`.' );
	}
	acc = incrmprod( W );

	// Create an iterator protocol-compliant object:
	iter = {};
	setReadOnly( iter, 'next', next );
	setReadOnly( iter, 'return', end );

	// If an environment supports `Symbol.iterator`, make the iterator iterable:
	if ( iteratorSymbol && isFunction( iterator[ iteratorSymbol ] ) ) {
		setReadOnly( iter, iteratorSymbol, factory );
	}
	return iter;

	/**
	* Returns an iterator protocol-compliant object containing the next iterated value.
	*
	* @private
	* @returns {Object} iterator protocol-compliant object
	*/
	function next() {
		var out;
		var v;
		if ( FLG ) {
			return {
				'done': true
			};
		}
		out = {};
		v = iterator.next();
		if ( typeof v.value === 'number' ) {
			out.value = acc( v.value );
		} else if ( hasOwnProp( v, 'value' ) ) {
			out.value = acc( NaN );
		}
		if ( v.done ) {
			FLG = true;
			out.done = true;
		} else {
			out.done = false;
		}
		return out;
	}

	/**
	* Finishes an iterator.
	*
	* @private
	* @param {*} [value] - value to return
	* @returns {Object} iterator protocol-compliant object
	*/
	function end( value ) {
		FLG = true;
		if ( arguments.length ) {
			return {
				'value': value,
				'done': true
			};
		}
		return {
			'done': true
		};
	}

	/**
	* Returns a new iterator.
	*
	* @private
	* @returns {Iterator} iterator
	*/
	function factory() {
		return itermprod( iterator[ iteratorSymbol ](), W );
	}
}


// EXPORTS //
export default itermprod;
